/* =====================================================================
 * cust-v6-sticky-buy.js — wires the mobile sticky bar to the real ATC
 *
 * The bar lives outside the product form. This script:
 *   1. Finds the real [data-add-to-cart] button rendered by Stiletto's
 *      product-form snippet.
 *   2. On sticky-button click, proxies the click to the real button so
 *      Shopify's existing add-to-cart pipeline runs unchanged (variant
 *      validation, AJAX submit, cart drawer, Recharge selling-plan
 *      attachment, _persona_id cart attribute writer, all of it).
 *   3. Mirrors the real button's disabled state + visible label into
 *      the sticky button via a MutationObserver.
 *   4. Re-runs on shopify:section:load so theme-editor section reloads
 *      don't leave a dead bar behind. (feedback_section_reload_safety.md)
 * ===================================================================== */
(function () {
  'use strict';

  var BOUND_FLAG = '__v6StickyBuyBound';

  // Brandwise's chat launcher (fixed, bottom 20px) sits under this bar.
  // While the bar is really visible, publish its height as --v6-sticky-h
  // plus body.v6-sticky-shown; cust-v6-sticky-buy.css lifts the launcher.
  function watchChatLift(bar) {
    function update() {
      var cs = window.getComputedStyle(bar);
      var h = bar.offsetHeight;
      var body = document.body;
      var shown = cs.display !== 'none' && cs.visibility !== 'hidden' && h > 0;
      if (shown) {
        body.style.setProperty('--v6-sticky-h', h + 'px');
      } else {
        body.style.removeProperty('--v6-sticky-h');
      }
      // Only touch the class on a real change: the body-class observer
      // below would otherwise re-trigger this function forever.
      if (body.classList.contains('v6-sticky-shown') !== shown) {
        body.classList.toggle('v6-sticky-shown', shown);
      }
    }
    update();
    if (window.ResizeObserver) new ResizeObserver(update).observe(bar);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true });
    var cart = document.getElementById('NativeCart');
    if (cart) {
      new MutationObserver(update).observe(cart, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
    }
    // The bar hides while Brandwise chat is open (body.bw-chat-open).
    new MutationObserver(update).observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function init(scope) {
    var root = scope || document;
    var bars = root.querySelectorAll('.v6-sticky-buy');
    if (!bars.length) return;

    bars.forEach(function (bar) {
      if (bar[BOUND_FLAG]) return;
      bar[BOUND_FLAG] = true;
      watchChatLift(bar);

      var ctaSticky = bar.querySelector('[data-v6-sticky-cta]');
      if (!ctaSticky) return;

      // Find the page's primary ATC. Default PDP uses [data-add-to-cart]
      // (Stiletto product-form). Shampoo/Conditioner PDP uses
      // [data-atc-button] (custom Single/Duo/Trio controller). Prefer the
      // custom one when present so the sticky bar follows the live total.
      function findRealCta() {
        return (
          document.querySelector('[data-atc-button]') ||
          document.querySelector('[data-product-form] [data-add-to-cart]') ||
          document.querySelector('[data-add-to-cart]')
        );
      }

      // On Shampoo/Conditioner PDPs the live total lives in [data-total]
      // and Stiletto's [data-price] updater is not wired. We mirror that
      // element's text into the sticky bar's [data-price] span.
      //
      // On the default PDP we do NOTHING — Stiletto's variant-change
      // handler already updates every [data-price] node on the page,
      // and our sticky bar's span has data-price too, so it follows
      // along for free. Mirroring [data-product-form] [data-price]
      // here was overwriting the sticky price with the wrong text.
      //
      // Default-section PDPs with the Subscribe & Save widget
      // (<subscription-widget>) are the exception: the price that gets
      // charged is the selected purchase option's .purchase-price, so we
      // mirror that. [data-total] still wins wherever it exists.
      function findWidgetPrice() {
        return document.querySelector('subscription-widget .purchase-option.selected .purchase-price');
      }

      function findPriceSource() {
        return document.querySelector('[data-total]') || findWidgetPrice();
      }

      function syncPrice() {
        var priceNode = bar.querySelector('[data-price]');
        var src = findPriceSource();
        if (!priceNode || !src || src === priceNode) return;

        // [data-total] on shampoo-conditioner PDPs renders as
        //   <span class="t-strike">$11.75</span>$9.40
        // when a sale is active. textContent would concatenate both
        // into "$11.75$9.40". Strip the strike span first so the
        // sticky bar shows only the current price.
        var clone = src.cloneNode(true);
        var strikes = clone.querySelectorAll('.t-strike, s, del, [data-compare-price]');
        for (var i = 0; i < strikes.length; i++) {
          strikes[i].parentNode.removeChild(strikes[i]);
        }
        var t = (clone.textContent || '').trim();
        // Only write on a real change: the widget path below observes this
        // node, so an unconditional write would re-trigger itself.
        if (t && priceNode.textContent !== t) priceNode.textContent = t;
      }

      function syncState() {
        var real = findRealCta();
        if (!real) {
          ctaSticky.setAttribute('disabled', '');
          ctaSticky.setAttribute('aria-disabled', 'true');
          return;
        }
        var disabled =
          real.hasAttribute('disabled') ||
          real.getAttribute('aria-disabled') === 'true';
        if (disabled) {
          ctaSticky.setAttribute('disabled', '');
          ctaSticky.setAttribute('aria-disabled', 'true');
        } else {
          ctaSticky.removeAttribute('disabled');
          ctaSticky.removeAttribute('aria-disabled');
        }

        // Mirror the real button's label into our [data-v6-sticky-label]
        // span ONLY. The sticky button now contains label + separator +
        // price spans, so a blanket textContent write would wipe them.
        var labelNode = real.querySelector('[data-add-to-cart-text]') || real;
        var labelText = (labelNode.textContent || '').trim();
        var stickyLabel = ctaSticky.querySelector('[data-v6-sticky-label]');
        if (labelText && stickyLabel) {
          stickyLabel.textContent = labelText;
        }
      }

      ctaSticky.addEventListener('click', function (e) {
        e.preventDefault();
        var real = findRealCta();
        if (!real || real.hasAttribute('disabled')) return;
        real.click();
      });

      syncState();
      syncPrice();

      // Watch the real button for attribute + text changes.
      var real = findRealCta();
      if (real) {
        var btnObs = new MutationObserver(syncState);
        btnObs.observe(real, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          characterData: true,
          subtree: true
        });
      }

      // Watch the live price source for text changes (variant /
      // Single-Duo-Trio / Subscribe-OneTime updates).
      var priceSrc = findPriceSource();
      var widget = document.querySelector('subscription-widget');
      if (priceSrc && !document.querySelector('[data-total]') && widget) {
        // Subscribe/One-Time switches move the .selected class, so watch
        // the whole widget. Stiletto's variant-change handler also rewrites
        // every [data-price] (ours included) to the one-time price on a
        // scent change, so watch our own span and re-assert after it.
        var widgetObs = new MutationObserver(syncPrice);
        widgetObs.observe(widget, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          characterData: true,
          subtree: true
        });
        var stickyPrice = bar.querySelector('[data-price]');
        if (stickyPrice) {
          new MutationObserver(syncPrice).observe(stickyPrice, {
            childList: true,
            characterData: true,
            subtree: true
          });
        }
      } else if (priceSrc) {
        var priceObs = new MutationObserver(syncPrice);
        priceObs.observe(priceSrc, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target);
  });
})();
